import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import { getCountryCallingCode } from "libphonenumber-js";
import { useDispatch, useSelector } from "react-redux";
import Typography from "../common-components/Typography";
import { font } from "../constants/font";
import Action from "../common-components/Action";
import openUrl from "../helper/openUrl";
import LabelDropdown from "../common-components/LabelDropdown";
import {
  CloseIcon,
  ContactSalesBody,
  ContactSalesFormWrap,
  Error,
} from "./animo-component";
import LabeledInput from "../common-components/LabeledInput";
import { contactSales, getIPData } from "../redux/actions/appUtils";
import content from "../constants/content";
import Backdrop from "../common-components/BackDrop";
import { STATIC_PATH } from "../constants/config";
import vmTheme from "../constants/theme";
import { BUSSINESS_DOMAINS } from "../constants";
import useMobileDetection from "../helper/hooks/useMobileDetection";

const Contact = ({ setShowContact }) => {
  const [formValue, setFormValue] = useState({
    name: "",
    businessEmail: "",
    companyName: "",
  });
  const [selectedCompanySize, setSelectedCompanySize] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [licenses, setLicenses] = useState("");
  const [teamHelp, setTeamHelp] = useState("");
  const [planning, setPlanning] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const isMobile = useMobileDetection();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const [formErrors, setFormErrors] = useState({
    name: "",
    businessEmail: "",
    companyName: "",
    businessType: " ",
    licenses: "",
    teamHelp: "",
    planning: "",
    phone: "",
    employee_count: "",
  });

  const theme = useSelector((state) => state.app.get('theme'));

  // helper: reject free/public domains
  const isFreeEmail = (email) => {
    const trimmedEmail = email?.trim() || '';
    const domain = trimmedEmail.split("@")[1]?.toLowerCase();
    const domainName = domain?.split(".")[0]?.toLowerCase();
    return BUSSINESS_DOMAINS.includes(domainName);
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Trim all values before checking
    const name = formValue.name.trim();
    const email = formValue.businessEmail.trim();
    const companyName = formValue.companyName.trim();
    const phoneNoValidate = phoneNumber.trim().replace(/\s+/g, '');

    if (!name) {
      errors.name = "Please enter your name";
      isValid = false;
    }

    if (!email) {
      errors.businessEmail = "Please enter your business email";
      isValid = false;
    } else if (isFreeEmail(email)) {
      errors.businessEmail = "Please enter a valid business email.";
      isValid = false;
    }

    if (!companyName) {
      errors.companyName = "Please enter your company";
      isValid = false;
    }

    if (businessType === "") {
      errors.businessType = "Please select any one option";
      isValid = false;
    }

    if (selectedCompanySize === "") {
      errors.employee_count = "Please select any one option";
      isValid = false;
    }

    if (licenses === "") {
      errors.licenses = "Please select any one option";
      isValid = false;
    }

    if (teamHelp === "") {
      errors.teamHelp = "Please select any one option";
      isValid = false;
    }

    if (planning === "") {
      errors.planning = "Please select any one option";
      isValid = false;
    }

    // Phone number validation
    if (!phoneNoValidate) {
      errors.phone = "Please enter your phone number";
      isValid = false;
    } else if (!/^\+?\d+$/.test(phoneNoValidate)) {
      errors.phone = "Phone number should contain numbers only and may start with a '+'";
      isValid = false;
    } else if (phoneNoValidate.length < 8 || phoneNoValidate.length > 15) {
      errors.phone = "Phone number should be between 8 and 15 digits";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  useEffect(() => {
    dispatch(getIPData()).then((res) => {
      const isoCode = res?.country_code || "IN";
      setCountryCode(isoCode);
      setCountryCode(`+${getCountryCallingCode(isoCode)}`);
    });
  }, [dispatch]);

  const handleDropdownChange = (selectedValue, setStateFunction, fieldName) => {
    setStateFunction(selectedValue.value);
    setFormErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
  };

  const closeContactForm = () => {
    setShowContact(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValue((prevFormValue) => ({
      ...prevFormValue,
      [name]: value,
    }));
    setFormErrors({ ...formErrors, [name]: "" });
  };

  const submitHandler = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      const reqBody = {
        Name: formValue.name,
        Business_email: formValue.businessEmail,
        Company: formValue.companyName,
        employee_count: selectedCompanySize,
        Business_type: businessType,
        licenses,
        sales_team_help: teamHelp,
        sales_plan: planning,
        phone: phoneNumber,
        country_code: countryCode,
        Source: "Editor"
      };

      contactSales(reqBody)
        .then(() => {
          setIsLoading(false);
          openUrl("https://contact.animaker.co/meeting/animaker/sakshicsachdev");
          closeContactForm();
        })
        // eslint-disable-next-line no-unused-vars
        .catch((_e) => {
          // _e
        });
    }
  };
  return (
    <>
      <Backdrop
        background="rgba(0,0,0,0.5)"
        zIndex={14}
        onClick={() => setShowContact(false)}
      />
      <ContactSalesFormWrap>
        <CloseIcon
          src={`${STATIC_PATH}close-icon.svg`}
          alt="close"
          onClick={() => setShowContact(false)}
          style={{ top: "0px", position: "absolute", right: "0px" }}
        />
        <Typography
          content={"Let's get you the best deal"}
          display="block"
          margin="0 auto"
          color={vmTheme[theme].contactLabelTextColor}
          align="center"
          font={font.boldSmall}
        />
        <ContactSalesBody>
          <LabeledInput
            label="Name"
            value={formValue.name}
            onChange={handleChange}
            type="text"
            name="name"
            required={true}
          />
          {formErrors.name && <Error>{formErrors.name}</Error>}
          <LabeledInput
            label="Business Email"
            value={formValue.businessEmail}
            onChange={handleChange}
            type="text"
            name="businessEmail"
            required={true}
          />
          {formErrors.businessEmail && <Error>{formErrors.businessEmail}</Error>}
          <LabeledInput
            label="Phone Number"
            value={phoneNumber}
            onChange={(e) => {
              const onlyNumbers = e.target.value.replace(/\D/g, "");
              setPhoneNumber(onlyNumbers);
              setFormErrors(prev => ({ ...prev, phone: "" }));
            }}
            type="tel"
            name="phone"
            required={true}
            rootMargin={0}
            isDisabled={false}
            className="input-class"
            countryCode={countryCode}
          />

          {formErrors.phone && (
            <Typography
              content={formErrors.phone}
              display="block"
              margin="2px 0 10px"
              color="#ff0000"
              align="left"
              font={font.normalMicroLarge}
            />
          )}
          <LabeledInput
            label="Company"
            value={formValue.companyName}
            onChange={handleChange}
            type="text"
            name="companyName"
            required={true}
          />
          {formErrors.companyName && <Error>{formErrors.companyName}</Error>}
          <LabelDropdown
            labelEnable
            width="100%"
            labelText="Business type"
            labelFont={font.normalBase_16}
            labelColor={vmTheme[theme].contactLabelColor}
            labelMargin="20px 0px 0px"
            enablePlaceholder
            optionWidth={"100%"}
            isBottom={isMobile}
            options={[
              { label: "Commercial Business", value: "commercial-business" },
              { label: "Educational Business", value: "educational-business" },
            ]}
            defaultValue={businessType}
            onChange={(value) =>
              handleDropdownChange(value, setBusinessType, "businessType")
            }
          />
          {formErrors.businessType && <Error>{formErrors.businessType}</Error>}
          <LabelDropdown
            labelEnable
            width="100%"
            labelText="Employee count"
            labelFont={font.normalBase_16}
            labelColor={vmTheme[theme].contactLabelColor}
            labelMargin="20px 0px 0px"
            optionWidth={"100%"}
            enablePlaceholder
            isBottom
            options={[
              { label: "1 - 10", value: "1 - 10" },
              { label: "11 - 50", value: "11 - 50" },
              { label: "51 - 200", value: "51 - 200" },
              { label: "201 - 1000", value: "201 - 1000" },
              { label: "1001 - 5000", value: "1001 - 5000" },
              { label: "5000+", value: "5000+" },
            ]}
            defaultValue={selectedCompanySize}
            onChange={(value) =>
              handleDropdownChange(
                value,
                setSelectedCompanySize,
                "selectedCompanySize"
              )
            }
          />
          {formErrors.employee_count && (
            <Error>{formErrors.employee_count}</Error>
          )}
          <LabelDropdown
            labelEnable
            optionWidth={"100%"}
            labelText="Required number of licenses"
            labelFont={font.normalBase_16}
            labelColor={vmTheme[theme].contactLabelColor}
            labelMargin="20px 0px 0px"
            enablePlaceholder
            options={[
              { label: "1 - 5", value: "1 - 5" },
              { label: "6 - 10", value: "6 - 10" },
              { label: "11 - 25", value: "11 - 25" },
              { label: "26 - 50", value: "26 - 50" },
              { label: "51+", value: "51+" },
            ]}
            defaultValue={licenses}
            isBottom
            onChange={(value) =>
              handleDropdownChange(value, setLicenses, "licenses")
            }
          />
          {formErrors.licenses && <Error>{formErrors.licenses}</Error>}
          <LabelDropdown
            labelEnable
            optionWidth={"100%"}
            labelText="How can our sales team help?"
            labelFont={font.normalBase_16}
            labelColor={vmTheme[theme].contactLabelColor}
            labelMargin="20px 0px 0px"
            options={[
              { label: "Discuss bulk purchase discounts", value: "Discuss bulk purchase discounts" },
              { label: "Arrange a meeting with sales representative", value: "Arrange a meeting with sales representative" },
              { label: "Provide support with new purchase process", value: "Provide support with new purchase process" },
              { label: "Provide assistance with renewals or upgrades", value: "Provide assistance with renewals or upgrades" },
              { label: "Provide assistance with any other enquiry", value: "Provide assistance with any other enquiry" },
            ]}
            defaultValue={teamHelp}
            enablePlaceholder
            isBottom
            onChange={(value) =>
              handleDropdownChange(value, setTeamHelp, "teamHelp")
            }
          />
          {formErrors.teamHelp && <Error>{formErrors.teamHelp}</Error>}
          <LabelDropdown
            labelEnable
            enablePlaceholder
            optionWidth={"100%"}
            labelText="What are you planning to use Vmaker for?"
            labelFont={font.normalBase_16}
            labelColor={vmTheme[theme].contactLabelColor}
            labelMargin="20px 0px 0px"
            options={[
              { label: "Learning and Development", value: "Learning and Development" },
              { label: "Marketing/Sales", value: "Marketing/Sales" },
              { label: "Internal Communications", value: "Internal Communications" },
              { label: "Personal Usage", value: "Personal Usage" },
              { label: "Customer Success", value: "Customer Success" },
              { label: "Others", value: "Others" },
            ]}
            defaultValue={planning}
            isBottom
            onChange={(value) =>
              handleDropdownChange(value, setPlanning, "planning")
            }
          />
          {formErrors.planning && <Error>{formErrors.planning}</Error>}
        </ContactSalesBody>
        <Action
          className="contact-btn"
          onClick={submitHandler}
          borderRadius="4px"
          text={content.CONTACT_SALES}
          marginTop="25px"
          isLoading={isLoading}
        />
      </ContactSalesFormWrap>
    </>
  );
};

Contact.propTypes = {
  setShowContact: PropTypes.func,
};

export default Contact;
